import { Button } from "@/components/ui/button"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import type { PermissionCatalog } from "@/types/team"
import { IconInfoCircle } from "@tabler/icons-react"
import { useMemo, useState } from "react"
import { PermissionMatrix } from "./PermissionMatrix"
import {
  closeDependencies,
  closeRemoval,
  flattenPermissions,
} from "./permissionResolver"
import { RoleBlockPicker } from "./RoleBlockPicker"

type EditorMode = "simple" | "advanced"

interface PermissionEditorProps {
  catalog: PermissionCatalog
  value: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
}

/**
 * Access editor with two lossless modes on the same permission set (R6):
 *  - Simple: block picker (6 stacked cards; BASE always on)
 *  - Advanced: per-permission matrix with dependency-aware toggles
 *
 * Tab switch keeps the resolved list intact. Advanced-mode diffs run through
 * closeDependencies / closeRemoval, so ticking a WRITE pulls in its READ and
 * unticking a READ cascades the WRITEs out — the matrix can't leave the user
 * in an unusable state (R4/F6). Every path through the editor keeps the
 * permission set self-consistent.
 */
export function PermissionEditor({
  catalog,
  value,
  onChange,
  disabled = false,
}: PermissionEditorProps) {
  const { translate } = useTranslation()
  const [mode, setMode] = useState<EditorMode>("simple")

  const totalKeys = useMemo(
    () => flattenPermissions(catalog).map((p) => p.key),
    [catalog],
  )
  const grantedCount = useMemo(
    () => value.filter((k) => totalKeys.includes(k)).length,
    [value, totalKeys],
  )
  const totalCount = totalKeys.length
  const baseCount = useMemo(
    () =>
      catalog.roleBlocks.find((b) => b.key === "BASE")?.permissions.length ?? 0,
    [catalog],
  )
  const beyondBase = Math.max(0, grantedCount - baseCount)

  // Advanced-mode diff: closes deps on grant, cascades on revoke. Every path
  // through here therefore leaves the permission set self-consistent (R4/F6).
  const handleAdvancedChange = (next: string[]) => {
    const before = new Set(value)
    const after = new Set(next)
    const added = next.filter((k) => !before.has(k))
    const removed = value.filter((k) => !after.has(k))
    let resolved = next
    if (added.length > 0) resolved = closeDependencies(resolved, catalog)
    if (removed.length > 0) resolved = closeRemoval(resolved, removed, catalog)
    onChange(resolved)
  }

  // Reset means "back to the floor": BASE + its dependency closure. Sending an
  // empty [] here would clear BASE too — but RoleBlockPicker renders BASE as
  // locked-and-matched regardless of `value`, so the admin would see BASE
  // still checked while the save persisted an empty grant set (member loses
  // their reads).
  const baseKeys = useMemo(
    () =>
      closeDependencies(
        catalog.roleBlocks.find((b) => b.key === "BASE")?.permissions ?? [],
        catalog,
      ),
    [catalog],
  )
  const handleReset = () => onChange(baseKeys)
  const canReset =
    !disabled &&
    (value.length !== baseKeys.length ||
      !baseKeys.every((k) => value.includes(k)))

  return (
    <div className="space-y-4">
      {/* Header — tabs on the left, counter + Clear on the right */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={mode} onValueChange={(v) => setMode(v as EditorMode)}>
          <TabsList className="h-9">
            <TabsTrigger value="simple" className="px-4 text-xs font-semibold">
              {translate("TEAM/EDITOR_MODE_SIMPLE")}
            </TabsTrigger>
            <TabsTrigger
              value="advanced"
              className="px-4 text-xs font-semibold"
            >
              {translate("TEAM/EDITOR_MODE_ADVANCED")}
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "rounded-full border px-3 py-1 text-xs font-medium tabular-nums",
              grantedCount === 0
                ? "border-border bg-transparent text-muted-foreground"
                : "border-primary/40 bg-primary/10 text-primary",
            )}
            aria-live="polite"
          >
            {translate("TEAM/GRANTED_OF_TOTAL", {
              count: grantedCount,
              total: totalCount,
            })}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={!canReset}
            onClick={handleReset}
            className="h-7 px-2 text-xs"
          >
            {translate("TEAM/PRESET_CLEAR")}
          </Button>
        </div>
      </div>

      {/* Info hint under the header — only in Simple mode, tells the admin
          that BASE is already in the count so the number isn't mysterious. */}
      {mode === "simple" && baseCount > 0 ? (
        <div className="flex items-start gap-2 rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          <IconInfoCircle
            size={14}
            className="mt-px shrink-0 text-muted-foreground/80"
          />
          <span>
            {beyondBase === 0
              ? translate("TEAM/EDITOR_HINT_BASE_ONLY", { count: baseCount })
              : translate("TEAM/EDITOR_HINT_WITH_BLOCKS", {
                  base: baseCount,
                  extra: beyondBase,
                })}
          </span>
        </div>
      ) : null}

      {/* Mode body */}
      {mode === "simple" ? (
        <RoleBlockPicker
          catalog={catalog}
          value={value}
          onChange={onChange}
          disabled={disabled}
        />
      ) : (
        <PermissionMatrix
          catalog={catalog}
          value={value}
          onChange={handleAdvancedChange}
          disabled={disabled}
        />
      )}
    </div>
  )
}
